import React, { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "./ui/button";
import { DownloadSimple, EnvelopeSimple, WifiHigh, CreditCard, ChatCircleText, WhatsappLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { api, formatUSD } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { parsePhoneNumberFromString } from "libphonenumber-js";

// Same +1 check as SMS — US/Canada are the two markets where WhatsApp
// adoption is low enough that showing the button mostly leads to a
// "Get WhatsApp" install page (bad UX). Hide it for those numbers and
// surface Share / Email / native SMS via Share sheet instead.
function isWhatsappLikelyForRecipient(phone) {
  if (!phone) return false;
  const norm = String(phone).replace(/[^\d+]/g, "");
  if (!norm.startsWith("+")) return false;       // bare 10-digit → assume US
  if (norm.startsWith("+1")) return false;       // US/Canada
  return true;
}

// NOTE (Feb 2026): Twilio Toll-Free Verification (TFV) is required by US
// carriers (AT&T / T-Mobile / Verizon) before they'll accept SMS from a
// toll-free number to US recipients. Until our TFV is approved, sending
// SMS to +1 numbers (US/Canada) returns Twilio error 30032. International
// recipients are unaffected — carriers outside US/CA don't enforce TFV.
//
// So we keep SMS enabled per-card but ONLY when the receiver's phone has
// a non-+1 country code. US/CA receivers get the Share button (sender's
// own WhatsApp / iMessage / native SMS) which sidesteps carrier rules.
function isSmsSupportedForRecipient(phone) {
  if (!phone) return false;
  const norm = String(phone).replace(/[^\d+]/g, "");
  // Bare 10-digit number with no country code → assume US (most common).
  if (!norm.startsWith("+")) return false;
  // +1 covers US + Canada — both blocked until TFV approves.
  if (norm.startsWith("+1")) return false;
  // Any other country code → SMS works without verification.
  return true;
}

const CHANNEL_PREFERENCE_BY_COUNTRY = {
  // Heavy WhatsApp markets — start there
  IN: ["whatsapp", "email", "sms"],
  BR: ["whatsapp", "sms", "email"],
  ID: ["whatsapp", "sms", "email"],
  MX: ["whatsapp", "sms", "email"],
  VN: ["whatsapp", "email", "sms"],
  PH: ["whatsapp", "sms", "email"],
  AR: ["whatsapp", "sms", "email"],
  CO: ["whatsapp", "sms", "email"],
  CL: ["whatsapp", "sms", "email"],
  TH: ["whatsapp", "sms", "email"],
  MY: ["whatsapp", "sms", "email"],
  SG: ["whatsapp", "sms", "email"],
  AE: ["whatsapp", "sms", "email"],
  SA: ["whatsapp", "sms", "email"],
  IL: ["whatsapp", "sms", "email"],
  TR: ["whatsapp", "sms", "email"],
  ZA: ["whatsapp", "sms", "email"],
  NG: ["whatsapp", "sms", "email"],
  EG: ["whatsapp", "sms", "email"],
  KE: ["whatsapp", "sms", "email"],
  // SMS-first markets — high deliverability, low WhatsApp share.
  // US/CA need Twilio TFV (still pending) — so SMS is filtered out at
  // runtime by isSmsSupportedForRecipient() for +1 phones, even though
  // listed here for future reactivation.
  US: ["sms", "whatsapp", "email"],
  CA: ["sms", "whatsapp", "email"],
  // Email-first markets — privacy/regulatory + good email infra
  GB: ["whatsapp", "email", "sms"],
  DE: ["email", "whatsapp", "sms"],
  FR: ["email", "whatsapp", "sms"],
  NL: ["email", "whatsapp", "sms"],
  SE: ["email", "whatsapp", "sms"],
  NO: ["email", "whatsapp", "sms"],
  DK: ["email", "whatsapp", "sms"],
  FI: ["email", "whatsapp", "sms"],
  AU: ["sms", "whatsapp", "email"],
  NZ: ["sms", "whatsapp", "email"],
  JP: ["email", "sms", "whatsapp"],   // LINE dominant — neither WhatsApp nor SMS great, prefer email
  KR: ["email", "sms", "whatsapp"],   // KakaoTalk dominant — same
};
const DEFAULT_CHANNEL_ORDER = ["whatsapp", "sms", "email"];

export default function VirtualCard({ card, onEmailed }) {
  const ref = useRef(null);
  const { isImpersonating } = useAuth();
  const [emailBlock, setEmailBlock] = React.useState(null); // { to_email, error }
  const [zoomed, setZoomed] = React.useState(false);
  // Cached signed 24h share URL. Pre-fetched on mount so the "Text card"
  // click handler can fire `sms:` SYNCHRONOUSLY — iOS Safari silently
  // blocks `window.location.href = 'sms:...'` when called after an `await`
  // because the transient user-gesture context has already expired.
  const [shareUrlCache, setShareUrlCache] = React.useState(null);
  const hasReceiverPhone = !!(card && card.receiver_phone && String(card.receiver_phone).replace(/\D/g, ""));

  // Eagerly fetch the share URL the moment we have an active card so that
  // by the time the sender taps "Text card" / "Share", we can hand off to
  // the native app synchronously. Cache survives prop refreshes for the
  // same card id.
  React.useEffect(() => {
    if (!card?.id || card.status !== "active") return undefined;
    let cancelled = false;
    api.post(`/cards/${card.id}/image-share-url`)
      .then((r) => {
        if (!cancelled && r?.data?.url) setShareUrlCache(r.data.url);
      })
      .catch(() => { /* will retry on first click */ });
    return () => { cancelled = true; };
  }, [card?.id, card?.status]);

  if (!card) return null;
  const masked = card.card_number
    ? card.card_number.replace(/(.{4})/g, "$1 ").trim()
    : "•••• •••• •••• ••••";
  const active = card.status === "active";

  const trackShare = (channel) => {
    // Fire-and-forget share-channel analytics. Never blocks the UX.
    api.post(`/cards/${card.id}/track-share`, { channel }).catch(() => {});
  };

  // Smart-send: try the receiver's country-optimal channel first, fall back
  // through the rest of the chain if that fails. Single-button UX so the
  // user doesn't have to think about MMS vs WhatsApp vs Email.
  const smartSend = async () => {
    const phone = (card.receiver_phone || "").trim();
    let countryOrder = DEFAULT_CHANNEL_ORDER;
    let countryCode = null;
    if (phone) {
      try {
        const parsed = parsePhoneNumberFromString(phone);
        if (parsed?.country) {
          countryCode = parsed.country;
          countryOrder = CHANNEL_PREFERENCE_BY_COUNTRY[countryCode] || DEFAULT_CHANNEL_ORDER;
        }
      } catch { /* fall back to default */ }
    } else if (card.receiver_email) {
      // No phone on file → email is the only viable channel
      countryOrder = ["email"];
    }

    // Filter out channels we don't have the inputs for (no phone → drop sms/whatsapp).
    // For SMS specifically, also strip it when the phone is a US/CA (+1)
    // number — Twilio TFV pending — see isSmsSupportedForRecipient().
    const viable = countryOrder.filter((c) => {
      if (c === "sms" && !isSmsSupportedForRecipient(phone)) return false;
      return (c === "email" && card.receiver_email) ||
             (c !== "email" && phone);
    });
    if (viable.length === 0) {
      toast.error("No phone or email on file — edit the card first");
      return;
    }

    const tryChannel = async (ch) => {
      if (ch === "sms") {
        const r = await api.post(`/cards/${card.id}/text`, {});
        return { ok: true, channel: "MMS", to: r.data.to_phone };
      }
      if (ch === "whatsapp") {
        const r = await api.post(`/cards/${card.id}/text-whatsapp`, {});
        return { ok: true, channel: "WhatsApp", to: r.data.to_phone };
      }
      if (ch === "email") {
        const r = await api.post(`/cards/${card.id}/email`, {});
        if (!r.data.sent) throw new Error(r.data.error || "Email rejected");
        return { ok: true, channel: "Email", to: r.data.to_email };
      }
      throw new Error(`Unknown channel: ${ch}`);
    };

    const errors = [];
    const banner = countryCode
      ? `Trying ${viable.join(" → ")} (optimized for ${countryCode})…`
      : `Trying ${viable.join(" → ")}…`;
    const loadingId = toast.loading(banner);

    for (const ch of viable) {
      try {
        const r = await tryChannel(ch);
        toast.success(`✓ Sent via ${r.channel} to ${r.to}`, {
          id: loadingId,
          duration: 8000,
          description: countryCode ? `Optimal channel for ${countryCode}` : undefined,
        });
        trackShare(`smart_${ch}`);
        return;
      } catch (err) {
        errors.push({ ch, detail: err?.response?.data?.detail || err?.message || "failed" });
      }
    }
    toast.error(
      `❌ All ${viable.length} channels failed. Last error: ${errors[errors.length - 1]?.detail}`,
      { id: loadingId, duration: 12000 }
    );
  };

  const download = async () => {
    // Robust blob-based download: works on iOS Safari, desktop Chrome,
    // mobile Chrome, Android browsers — anywhere `window.open` of an
    // image triggers "view" not "save". We fetch the PNG bytes ourselves,
    // wrap in a synthetic <a download="..."> click, then revoke the URL.
    const last4 = (card.card_number || "").slice(-4) || card.id.slice(0, 4);
    try {
      // Get the signed URL (no auth needed → can fetch directly)
      const r = await api.post(`/cards/${card.id}/image-download-url`);
      const path = r?.data?.path;
      const url = path ? `${window.location.origin}${path}` : r?.data?.url;
      if (!url) throw new Error("No download URL returned");

      // Fetch the PNG bytes
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) throw new Error(`Image fetch failed: HTTP ${res.status}`);
      const blob = await res.blob();
      if (blob.size < 100) throw new Error("Downloaded file is empty");

      // Trigger save via synthetic anchor (works on iOS Safari)
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `zcardon-${last4}.png`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      // Cleanup after a tick so the browser captures the click
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        a.remove();
      }, 200);
      trackShare("download");
      toast.success(`✓ Saved zcardon-${last4}.png to Downloads`, { duration: 6000 });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not download — please retry.";
      console.error("Download failed:", err, detail);
      toast.error(`Download failed — ${detail}`, { duration: 12000 });
    }
  };

  const copyShareLink = async () => {
    try {
      const r = await api.post(`/cards/${card.id}/image-share-url`);
      const url = r?.data?.url;
      if (!url) throw new Error("No share URL returned");
      try {
        await navigator.clipboard.writeText(url);
        trackShare("share_link");
        toast.success("Share link copied — paste into WhatsApp, iMessage, Signal (valid 24h)");
      } catch {
        // Fallback for browsers without clipboard permission
        window.prompt("Copy this link (valid 24h):", url);
        trackShare("share_link");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not generate share link — please retry.";
      console.error("Share link failed:", err, detail);
      toast.error(detail);
    }
  };

  // Open the device's native share sheet (Web Share API). The sender then
  // picks any messaging app installed — WhatsApp / iMessage / Signal /
  // Telegram / Email / Copy link / etc. — and ships the card link from
  // their own device. This sidesteps every carrier-side issue (Twilio
  // toll-free verification, A2P 10DLC, international routing) because
  // we're not the sender of the SMS — they are.
  const nativeShare = async () => {
    let shareUrl;
    try {
      const r = await api.post(`/cards/${card.id}/image-share-url`);
      shareUrl = r?.data?.url;
      if (!shareUrl) throw new Error("No share URL returned");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not generate share link — please retry.";
      toast.error(detail);
      return;
    }

    const name = card.receiver_name ? `, ${card.receiver_name.split(" ")[0]}` : "";
    const shareData = {
      title: "Your Zcardon card",
      text: `Hi${name}! Here's your Zcardon card 💳 — link valid 24 hours.`,
      url: shareUrl,
    };

    if (navigator.share && navigator.canShare?.(shareData) !== false) {
      try {
        await navigator.share(shareData);
        trackShare("native_share");
        toast.success("✓ Shared", { duration: 4000 });
        return;
      } catch (err) {
        // User cancelled — silent return. Anything else: fall through to copy.
        if (err?.name === "AbortError") return;
      }
    }
    // Browsers without Web Share API (older desktop) → copy + tell user.
    try {
      await navigator.clipboard.writeText(shareUrl);
      trackShare("native_share_copy");
      toast.success(
        "Link copied — paste into WhatsApp, iMessage, Telegram, Email, or any chat app",
        { duration: 8000 }
      );
    } catch {
      window.prompt("Copy this link (valid 24h) and paste it anywhere:", shareUrl);
      trackShare("native_share_prompt");
    }
  };

  // Open the sender's native SMS app (Messages / iMessage) prefilled with
  // the receiver's phone number and a 24h card link. The text sends from
  // the SENDER's own phone number — peer-to-peer, no carrier verification
  // required. Perfect for US/Canada recipients while Twilio Toll-Free
  // Verification is pending (Error 30032 only blocks A2P toll-free sends).
  //
  // CRITICAL: this handler MUST stay synchronous from the user tap. iOS
  // Safari + PWA standalone silently block `sms:` navigation that happens
  // after an `await` because transient user activation has expired. We
  // therefore rely on `shareUrlCache` (pre-fetched on card mount) and never
  // await before clicking the synthetic anchor.
  const composeNativeSms = () => {
    const phone = (card.receiver_phone || "").trim();
    if (!phone) {
      toast.error("No phone on file — edit the card to add one");
      return;
    }
    // If the signed share URL hasn't fetched yet, fall back to a public
    // landing URL we can construct synchronously from card.id. iOS Safari
    // strictly requires that the `sms:` anchor click happen inside the
    // same user-gesture tick — we MUST NOT `await` here. Re-firing the
    // fetch in the background updates the cache for next time, but the
    // first tap always opens Messages.
    let link = shareUrlCache;
    if (!link) {
      const origin = (typeof window !== "undefined" && window.location?.origin) || "";
      link = `${origin}/cards/${card.id}`;
      // background refetch so the next tap has the richer signed URL
      api.post(`/cards/${card.id}/image-share-url`)
        .then((r) => { if (r?.data?.url) setShareUrlCache(r.data.url); })
        .catch(() => {});
    }

    const name = card.receiver_name ? `, ${card.receiver_name.split(" ")[0]}` : "";
    const body = `Hi${name}! Here's your Zcardon card 💳\n${link}\n\n(Link valid 24 hours)`;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
    // iOS 13+ and Android both accept `?body=`. Older iOS expects `&body=`
    // but we don't support those carriers anyway.
    const smsHref = `sms:${phone}?body=${encodeURIComponent(body)}`;

    if (isMobile) {
      // Programmatic anchor click — works in iOS Safari, Android Chrome,
      // AND installed PWA standalone mode. `window.location.href = sms:...`
      // does NOT — it's the failure mode the user reported.
      const a = document.createElement("a");
      a.href = smsHref;
      a.target = "_self";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { try { a.remove(); } catch { /* noop */ } }, 200);

      trackShare("native_sms_compose");
      toast.success(
        `Opening Messages — tap Send to text ${card.receiver_name || phone}`,
        { duration: 8000 }
      );

      // Safety net: if iOS silently refused to open Messages (PWA edge
      // case, in-app webview, or stale gesture), the page stays visible
      // 2.5 s later. Detect that, copy the link, and tell the user how to
      // proceed manually.
      setTimeout(() => {
        if (typeof document !== "undefined" && !document.hidden) {
          if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(link).then(
              () => {
                trackShare("native_sms_clipboard_fallback");
                toast.info(
                  `Messages didn't open — link copied. Paste it into Messages and send to ${phone}.`,
                  { duration: 14000 }
                );
              },
              () => { /* clipboard blocked, give up silently */ }
            );
          }
        }
      }, 2500);
      return;
    }

    // Desktop: sms: schemes don't open Messages reliably. Copy the link and
    // give the sender a one-click action to send via WhatsApp Web (most
    // common alternative on US desktops) so they don't have to pick up
    // their phone. wa.me redirects to web.whatsapp.com on desktop browsers
    // when the user is logged in, or shows a "Continue to chat" page
    // otherwise. The receiver's phone digits are pre-resolved into the URL.
    const phoneDigits = phone.replace(/\D/g, "");
    const waText = `Hi${name}! Here's your Zcardon card 💳\n${link}\n\n(Link valid 24 hours)`;
    const waUrl = phoneDigits
      ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(waText)}`
      : `https://wa.me/?text=${encodeURIComponent(waText)}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(
        () => {
          trackShare("native_sms_desktop_copy");
          toast.success(
            `Link copied — text it to ${phone} from your phone, or send via WhatsApp now (link valid 24h)`,
            {
              duration: 12000,
              action: {
                label: "Open WhatsApp Web",
                onClick: () => {
                  window.open(waUrl, "_blank", "noopener");
                  trackShare("native_sms_desktop_whatsapp_web");
                },
              },
            }
          );
        },
        () => {
          trackShare("native_sms_desktop_prompt");
          window.prompt(`Copy this link and text it to ${phone}:`, link);
        }
      );
    } else {
      trackShare("native_sms_desktop_prompt");
      window.prompt(`Copy this link and text it to ${phone}:`, link);
    }
  };

  const sendViaWhatsapp = async () => {
    // Smart routing: if Twilio WhatsApp is configured AND we have a phone on
    // file, send the card directly via the backend (no app switch). Otherwise
    // fall back to opening wa.me with a pre-filled share link.
    const phoneDigits = (card.receiver_phone || "").replace(/\D/g, "");
    const tryDirect = !!phoneDigits;
    if (tryDirect) {
      try {
        const r = await api.post(`/cards/${card.id}/text-whatsapp`, {});
        trackShare("whatsapp_sms");
        toast.success(
          `✓ Sent via WhatsApp to ${r.data.to_phone}`,
          { duration: 8000 }
        );
        return;
      } catch (err) {
        const detail = err?.response?.data?.detail || "";
        // 503 = TWILIO_WHATSAPP_FROM not configured → silently fall back to wa.me
        // anything else (channel not enabled, 24h window, etc.) → tell user
        // and STILL fall back so they can ship the card today.
        const isUnconfigured = err?.response?.status === 503;
        if (!isUnconfigured) {
          toast.warning(
            `${detail || "WhatsApp Business send failed"} — opening WhatsApp share link instead`,
            { duration: 6000 }
          );
        }
      }
    }
    // Fallback: open wa.me share link
    try {
      const r = await api.post(`/cards/${card.id}/image-share-url`);
      const url = r?.data?.url;
      if (!url) throw new Error("No share URL returned");
      const name = card.receiver_name ? `, ${card.receiver_name.split(" ")[0]}` : "";
      const text = `Hi${name}! Here's your Zcardon card 💳\n${url}\n\n(Link valid 24 hours. Save the image to keep it.)`;
      const wa = phoneDigits
        ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(text)}`
        : `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(wa, "_blank", "noopener");
      trackShare("whatsapp");
      toast.success(phoneDigits ? `Opened WhatsApp with ${card.receiver_name || "receiver"} prefilled` : "Opened WhatsApp — pick a contact");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not open WhatsApp — please retry.";
      console.error("WhatsApp share failed:", err, detail);
      toast.error(detail);
    }
  };

  const emailReceiver = async () => {
    try {
      const r = await api.post(`/cards/${card.id}/email`, {});
      if (r.data.sent) {
        trackShare("email");
        toast.success(`Sent to ${r.data.to_email}`);
        onEmailed && onEmailed();
        return;
      }
      // Backend returned sent=false with a descriptive error. Open a helpful dialog
      // instead of a fleeting toast so the user knows what to do next.
      setEmailBlock({ to_email: r.data.to_email || card.receiver_email, error: r.data.error || "Email could not be sent" });
      onEmailed && onEmailed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    }
  };

  const textCard = async () => {
    try {
      const r = await api.post(`/cards/${card.id}/text`, {});
      trackShare("sms");
      const sid = r.data.sid;
      const status = r.data.status;
      const isTollFree = r.data.is_toll_free;

      // Initial response — Twilio almost always returns "queued" first.
      // Tell the user the message is in flight, but warn that delivery is async.
      toast.info(
        `Submitted to Twilio (status: ${status}). Checking actual delivery in 8s…` +
        (isTollFree ? " ⚠️ Note: your sender number is toll-free — US carriers may silently drop messages until you complete Toll-Free Verification." : ""),
        { duration: 12000 }
      );

      // Poll for the actual delivery status after 8s (carriers typically report within 5-15s)
      setTimeout(async () => {
        try {
          const s = await api.get(`/cards/${card.id}/text-status/${sid}`);
          const d = s.data;
          if (d.delivered) {
            toast.success(`✓ Delivered to ${r.data.to_phone}`, { duration: 8000 });
          } else if (d.fallback?.fired) {
            // Auto-fallback fired — receiver gets card details as text-only SMS.
            toast.success(
              `✓ Re-sent as text-only SMS — image attachment rejected by carrier, but card details (number, expiry, CVV, balance) are in the message body. New status: ${d.fallback.status}.`,
              { duration: 18000 }
            );
          } else if (d.final) {
            // undelivered / failed — show the fix hint + offer WhatsApp fallback if configured
            const errSummary = d.error_code ? `Error ${d.error_code}: ${d.error_message || "(no detail)"}` : d.status;
            toast.error(
              `❌ Delivery failed — ${errSummary}\n\n${d.fix_hint || "Check Twilio Console > Monitor > Logs for details."}`,
              {
                duration: 30000,
                action: {
                  label: "Try WhatsApp",
                  onClick: () => textCardWhatsapp(),
                },
              }
            );
          } else {
            toast.info(`Still ${d.status}. Twilio is retrying — check back in a minute.`, { duration: 8000 });
          }
        } catch (e) {
          // Status check itself failed — non-fatal
          toast.warning(`Could not verify delivery — check Twilio Console for SID ${sid.slice(0, 12)}…`, { duration: 8000 });
        }
      }, 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not text the card", { duration: 10000 });
    }
  };

  const textCardWhatsapp = async () => {
    try {
      const r = await api.post(`/cards/${card.id}/text-whatsapp`, {});
      trackShare("whatsapp_sms");
      toast.success(`Sent via WhatsApp (SID ${r.data.sid.slice(0, 12)}…) to ${r.data.to_phone}`, { duration: 8000 });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send via WhatsApp";
      toast.error(detail, { duration: 12000 });
    }
  };

  // QR payload — upgrade legacy VIETQR|... format to a proper deep-link URL so phone cameras
  // open the Track page instead of reporting "code is invalid".
  const rawQr = card.qr_payload || "";
  let qrUrl = "";
  if (rawQr && !rawQr.startsWith("VIETQR|")) {
    qrUrl = rawQr;
  } else {
    const num = card.card_number || "";
    const last4 = num.slice(-4);
    const exp = card.expiry || "";
    if (last4.length === 4 && exp) {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      qrUrl = `${base}/pay?last4=${last4}&exp=${encodeURIComponent(exp)}&s=scan`;
    }
  }

  return (
    <div className="space-y-4">
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        onClick={() => card.inline_image_url && setZoomed(true)}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && card.inline_image_url) {
            e.preventDefault();
            setZoomed(true);
          }
        }}
        className={`relative rounded-3xl overflow-hidden shadow-2xl text-[#FBF9F6] aspect-[1.586/1] max-w-md ${card.inline_image_url ? "cursor-zoom-in hover:shadow-[0_25px_60px_-15px_rgba(10,31,22,0.45)] transition-shadow" : ""}`}
        style={{ background: "linear-gradient(135deg, #0A1F16 0%, #153D28 55%, #0A1F16 100%)" }}
        data-testid="virtual-card-preview"
        title={card.inline_image_url ? "Tap to view full card image" : ""}
      >
        <div className="absolute inset-0" style={{ background: "radial-gradient(120% 100% at 0% 0%, rgba(217,108,74,0.18) 0%, transparent 55%), radial-gradient(80% 80% at 100% 100%, rgba(139,168,136,0.18) 0%, transparent 60%)" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#0A1F16]/60" />
        {/* A/B layout dispatcher — `card_layout_version` is randomly assigned
            at issue time (50/50 in /api/cards/issue). Older cards without
            the field default to v2 (the new big-QR design). */}
        {(card.card_layout_version || "v2-big-qr").startsWith("v1") ? (
          /* v1-small-qr — legacy layout: prominent 16pt PAN, small 72px QR
             tucked bottom-right next to EXP·CVV. Kept alive for A/B scan-rate
             measurement. */
          <div className="relative h-full flex flex-col justify-between p-6" data-testid="card-layout-v1">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center" data-testid="card-brand">
                  <img
                    src="/zcardon-logo-dark.png"
                    alt="Cardon"
                    className="h-9 md:h-10 w-auto select-none"
                    draggable="false"
                  />
                </div>
                {card.issuer_name && (
                  <div className="mt-3">
                    <p className="text-[10px] opacity-60 tracking-widest">ISSUED BY</p>
                    <p className="text-sm tracking-wider" data-testid="card-issuer">{card.issuer_name}</p>
                  </div>
                )}
              </div>
              <WifiHigh size={28} weight="duotone" className="opacity-80 rotate-90" />
            </div>

            <div>
              <p className="mono text-xl md:text-2xl tracking-[0.18em] font-semibold" data-testid="card-number">
                {masked}
              </p>
              <div className="flex items-end justify-between mt-3 gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] opacity-60 tracking-widest">CARDHOLDER</p>
                  <p className="text-base uppercase tracking-wider truncate" data-testid="card-holder">{card.receiver_name}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] opacity-60 tracking-widest">EXP · CVV</p>
                  <p className="mono text-base font-semibold" data-testid="card-exp-cvv">{card.expiry || "••/••"} · {card.cvv || "•••"}</p>
                </div>
                <div className="bg-white rounded-lg p-1.5 shrink-0" data-testid="card-qr-block">
                  {active && qrUrl ? (
                    <QRCodeSVG value={qrUrl} size={72} fgColor="#0A1F16" bgColor="#FFFFFF" />
                  ) : (
                    <div className="w-[72px] h-[72px] flex items-center justify-center text-[#0A1F16]/30">
                      <CreditCard size={34} weight="duotone" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* v2-big-qr — new layout: compact PAN on the left, dominant 160px
             QR centered on the right for max real-world scannability. */
          <div className="relative h-full flex p-5 gap-3" data-testid="card-layout-v2">
            <div className="flex-1 min-w-0 flex flex-col justify-between">
              <div data-testid="card-brand">
                <img
                  src="/zcardon-logo-dark.png"
                  alt="Cardon"
                  className="h-8 md:h-9 w-auto select-none"
                  draggable="false"
                />
                {card.issuer_name && (
                  <div className="mt-2">
                    <p className="text-[9px] opacity-60 tracking-widest">ISSUED BY</p>
                    <p className="text-xs tracking-wider" data-testid="card-issuer">{card.issuer_name}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="mono text-[11px] md:text-sm tracking-[0.16em] font-semibold" data-testid="card-number">
                  {masked}
                </p>
                <div className="flex items-end justify-between mt-2 gap-2">
                  <div className="min-w-0">
                    <p className="text-[9px] opacity-60 tracking-widest">CARDHOLDER</p>
                    <p className="text-xs uppercase tracking-wider truncate" data-testid="card-holder">{card.receiver_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[9px] opacity-60 tracking-widest">EXP · CVV</p>
                    <p className="mono text-xs font-semibold" data-testid="card-exp-cvv">{card.expiry || "••/••"}·{card.cvv || "•••"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="self-center bg-white rounded-lg p-2 shrink-0" data-testid="card-qr-block">
              {active && qrUrl ? (
                <QRCodeSVG value={qrUrl} size={160} fgColor="#0A1F16" bgColor="#FFFFFF" />
              ) : (
                <div className="w-[160px] h-[160px] flex items-center justify-center text-[#0A1F16]/30">
                  <CreditCard size={56} weight="duotone" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {active && (() => {
        // Smart UX: pick the best PRIMARY CTA based on what contact info we
        // have AND whether the receiver phone is US/CA (where Twilio A2P
        // toll-free SMS is blocked by carriers until TFV approves) AND
        // whether the SENDER is on mobile (can fire native sms:) or desktop
        // (can't — desktop browsers physically can't open Messages).
        //   - US/CA phone, sender on mobile  → "Text card" via native sms:
        //     Peer-to-peer SMS that bypasses every Twilio carrier rule.
        //   - US/CA phone, sender on desktop + receiver has email
        //                                    → Auto-email card via Resend.
        //                                      (Desktop can't sms:, so don't
        //                                       pretend. Email is the real
        //                                       working channel here.)
        //   - US/CA phone, sender on desktop + no email
        //                                    → Share (copy + WhatsApp web)
        //   - Phone (non-US) + email         → Smart Send (Twilio auto-routes).
        //   - Email only                     → Email.
        //   - Phone only (non-US)            → Share sheet.
        //   - Neither                        → no primary CTA, Download/Copy.
        const hasEmail = !!(card.receiver_email || "").trim();
        const phoneNorm = (card.receiver_phone || "").replace(/[^\d+]/g, "");
        const isUsCaRecipient = hasReceiverPhone && (!phoneNorm.startsWith("+") || phoneNorm.startsWith("+1"));
        const isMobileSender = typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");

        let primary;
        if (hasReceiverPhone && isUsCaRecipient && isMobileSender) {
          // iPhone / Android sender, US/CA receiver — use native iMessage.
          primary = "native_sms";
        } else if (hasReceiverPhone && isUsCaRecipient && hasEmail) {
          // Desktop sender, US/CA receiver — backend SMS is blocked by TFV,
          // so auto-email is the only working channel that delivers the
          // actual card PNG to the receiver in one click.
          primary = "email";
        } else if (hasReceiverPhone && hasEmail) {
          primary = "smart";
        } else if (hasEmail && !hasReceiverPhone) {
          primary = "email";
        } else if (hasReceiverPhone) {
          primary = "share";
        } else {
          primary = "none";
        }

        const receiverFirst = card.receiver_name?.split(" ")[0] || "your receiver";
        const hint = primary === "native_sms"
          ? `📱 Tap "Text card" — opens Messages on your phone with ${receiverFirst}'s number and the card link prefilled. Tap Send and done.`
          : primary === "email"
            ? (hasReceiverPhone && isUsCaRecipient && !isMobileSender
                ? `📧 Tap "Email card" — sends the card PNG to ${card.receiver_email} via Resend. (US carrier SMS rules block toll-free → US numbers; open this page on your iPhone to text instead.)`
                : `📧 Best for ${receiverFirst} — they only have email on file.`)
            : primary === "share"
              ? "📱 Tap Share — pick Messages (works on any phone), WhatsApp, Telegram, or any app your receiver uses."
              : primary === "smart"
                ? "Auto-picks the best channel — or tap Share to choose yourself."
                : "No contact info on file. Download the PNG and hand it to your receiver, or copy the share link.";
        const filled = "bg-[#D96C4A] hover:bg-[#B04E2F] text-white rounded-xl shadow-sm disabled:opacity-50";
        const outlined = "border-[#184A30] text-[#184A30] rounded-xl disabled:opacity-50";
        return (
          <div className="space-y-2">
            <p className="text-xs text-[#4A5C50] mono leading-relaxed" data-testid="delivery-hint">
              {hint}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="mono text-sm text-[#4A5C50]">
                Balance: <span className="text-[#184A30] font-semibold">{formatUSD(card.balance_vnd ?? card.quote?.principal_vnd)}</span>
              </div>
              <div className="flex-1" />
              {primary === "native_sms" && (
                <Button
                  className={filled}
                  onClick={composeNativeSms}
                  data-testid="native-sms-btn"
                  title={`Open Messages with ${card.receiver_phone} and the card link prefilled — sends from your phone`}
                >
                  <ChatCircleText size={18} weight="duotone" className="mr-1" /> Text card
                </Button>
              )}
              {primary === "smart" && (
                <Button
                  className={filled}
                  onClick={smartSend}
                  disabled={isImpersonating}
                  title={isImpersonating ? "View-as mode is read-only" : "Auto-pick the best channel for the receiver's country"}
                  data-testid="smart-send-btn"
                >
                  <PaperPlaneTilt size={18} weight="duotone" className="mr-1" /> Send card
                </Button>
              )}
              {primary === "email" && (
                <Button
                  className={filled}
                  onClick={emailReceiver}
                  disabled={isImpersonating}
                  title={isImpersonating ? "View-as mode is read-only" : `Send the card PNG to ${card.receiver_email} via Resend`}
                  data-testid="email-card-btn"
                >
                  <EnvelopeSimple size={18} className="mr-1" /> Email card
                </Button>
              )}
              <Button variant="outline" className={outlined} onClick={download} data-testid="download-card-btn">
                <DownloadSimple size={18} className="mr-1" /> Download PNG
              </Button>
              {primary !== "email" && hasEmail && (
                <Button
                  variant="outline"
                  className={outlined}
                  onClick={emailReceiver}
                  disabled={isImpersonating}
                  title={isImpersonating ? "View-as mode is read-only" : "Email delivery requires a verified Resend domain"}
                  data-testid="email-card-btn"
                >
                  <EnvelopeSimple size={18} className="mr-1" /> Email receiver
                </Button>
              )}
              {isSmsSupportedForRecipient(card.receiver_phone) && (
                <Button variant="outline" className={outlined} onClick={textCard} disabled={isImpersonating} title={isImpersonating ? "View-as mode is read-only" : undefined} data-testid="text-card-btn">
                  <ChatCircleText size={18} className="mr-1" /> Text card
                </Button>
              )}
              {isWhatsappLikelyForRecipient(card.receiver_phone) && (
                <Button
                  className="bg-[#25D366] hover:bg-[#1EA855] text-white rounded-xl shadow-sm"
                  onClick={sendViaWhatsapp}
                  data-testid="whatsapp-share-btn"
                  title="Open WhatsApp with the card link pre-filled"
                >
                  <WhatsappLogo size={18} weight="fill" className="mr-1" /> WhatsApp
                </Button>
              )}
            </div>
          </div>
        );
      })()}
      {!active && (
        <p className="text-sm text-[#4A5C50]">Card will activate once payment completes.</p>
      )}

      {/* Email delivery blocked dialog — shown when backend returns sent=false */}
      <Dialog open={!!emailBlock} onOpenChange={(v) => { if (!v) setEmailBlock(null); }}>
        {emailBlock && (
          <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-md" data-testid="email-blocked-dialog">
            <DialogHeader>
              <DialogTitle className="serif text-2xl">Email could not be delivered</DialogTitle>
              <DialogDescription className="text-[#4A5C50]">
                Our email provider rejected the delivery to <span className="mono text-[#112318]">{emailBlock.to_email}</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-4">
              <div className="bg-[#D96C4A]/10 border border-[#D96C4A]/30 rounded-xl p-3 text-sm text-[#B04E2F] leading-relaxed">
                {emailBlock.error}
              </div>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-2">Deliver the card right now</p>
                <div className="flex flex-wrap gap-2">
                  {hasReceiverPhone && (() => {
                    const pn = (card.receiver_phone || "").replace(/[^\d+]/g, "");
                    const isUsCa = !pn.startsWith("+") || pn.startsWith("+1");
                    return isUsCa ? (
                      <Button onClick={() => { setEmailBlock(null); composeNativeSms(); }} variant="outline" className="rounded-xl border-[#184A30] text-[#184A30]" data-testid="email-blocked-native-sms">
                        <ChatCircleText size={14} className="mr-1" /> Text from my phone
                      </Button>
                    ) : null;
                  })()}
                  {hasReceiverPhone && (
                    <Button onClick={() => { setEmailBlock(null); sendViaWhatsapp(); }} className="bg-[#25D366] hover:bg-[#1EA855] text-white rounded-xl" data-testid="email-blocked-whatsapp">
                      <WhatsappLogo size={14} weight="fill" className="mr-1" /> WhatsApp
                    </Button>
                  )}
                  {isSmsSupportedForRecipient(card.receiver_phone) && (
                    <Button onClick={() => { setEmailBlock(null); textCard(); }} variant="outline" className="rounded-xl border-[#184A30] text-[#184A30]" data-testid="email-blocked-sms">
                      <ChatCircleText size={14} className="mr-1" /> SMS
                    </Button>
                  )}
                  <Button onClick={() => { setEmailBlock(null); download(); }} variant="outline" className="rounded-xl border-[#184A30] text-[#184A30]" data-testid="email-blocked-download">
                    <DownloadSimple size={14} className="mr-1" /> Download PNG
                  </Button>
                </div>
                {!hasReceiverPhone && (
                  <p className="text-[11px] text-[#4A5C50] mt-2 italic">Add a phone number to the card to unlock WhatsApp / SMS delivery.</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEmailBlock(null)} data-testid="email-blocked-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Tap-to-zoom lightbox: shows the rendered card PNG at native
          resolution so the sender can present it to the receiver in person
          (handoff for tap-to-pay, or recovery if the receiver lost their email). */}
      <Dialog open={zoomed} onOpenChange={setZoomed}>
        <DialogContent
          className="max-w-3xl bg-[#0A1F16] border-none p-0 overflow-hidden"
          data-testid="card-zoom-dialog"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Full card image</DialogTitle>
          </DialogHeader>
          {card.inline_image_url && (
            <img
              src={card.inline_image_url}
              alt={`Zcardon virtual card for ${card.receiver_name}`}
              className="w-full h-auto select-none"
              draggable="false"
              data-testid="card-zoom-image"
            />
          )}
          <div className="bg-[#FBF9F6] px-5 py-3 flex items-center justify-between gap-2">
            <p className="text-xs text-[#4A5C50]">
              This is the exact image emailed/texted to {card.receiver_name}.
            </p>
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={download}
                className="rounded-xl border-[#184A30] text-[#184A30] text-xs"
                data-testid="card-zoom-download"
              >
                <DownloadSimple size={14} className="mr-1" /> Download PNG
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setZoomed(false)}
                className="text-[#4A5C50] text-xs"
                data-testid="card-zoom-close"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
